import {
  AfterViewInit,
  Component,
  Inject,
  ViewChild,
  WritableSignal,
  signal
} from "@angular/core";
import { POPUP_INJECT, PopupRef } from "src/app/services/popup/popup-ref";
import { PopupCreateEntityResolvedConfig } from "src/app/services/popup/popup-create-entity-config";
import DevExpress from "devextreme";
import {
  PopupButton,
  PopupComponent
} from "src/app/services/popup/popup-component";
import { DxFormComponent } from "devextreme-angular";
import { uuid } from "src/app/support/uuid";
import { isFunction } from "@jsverse/transloco";
import { ButtonClickAsyncEvent } from "src/app/components/ui/button/button-click-async-event";
import { find, get, set } from "lodash-es";
import { flattenTree } from "src/app/support/array";
import { Bind } from "src/app/support/decorators/bind";
import { sntTranslate } from "src/app/support/translate";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { FieldDataChangedEvent } from "devextreme/ui/form";
import { BehaviorSubject } from "rxjs";

type FormItem =
  | DevExpress.ui.dxFormSimpleItem
  | DevExpress.ui.dxFormGroupItem
  | DevExpress.ui.dxFormTabbedItem
  | DevExpress.ui.dxFormEmptyItem
  | DevExpress.ui.dxFormButtonItem;

@Component({
  selector: "pms-create-entity",
  templateUrl: "create-entity.component.html",
  styles: [
    `
      :host {
        display: flex;
        flex: 1;
      }
    `
  ],
  standalone: false
})
export class CreateEntityComponent implements AfterViewInit, PopupComponent {
  @ViewChild(DxFormComponent)
  dxForm!: DxFormComponent;

  formData = {};

  editedFields: Set<string> = new Set();

  items: DevExpress.ui.dxForm.SimpleItem[];

  buttons: WritableSignal<PopupButton[]>;

  secondaryButtons: WritableSignal<PopupButton[]>;

  dirty: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  popupTitle: WritableSignal<string>;

  enableMultipleCreate: boolean = false;
  multipleCreateLabel: string | undefined;

  constructor(
    @Inject(POPUP_INJECT.REF) private popupRef: PopupRef<any>,
    @Inject(POPUP_INJECT.DATA)
    public data: PopupCreateEntityResolvedConfig<any>
  ) {
    this.buttons = signal<PopupButton[]>([]);
    this.popupTitle = signal<string>(
      isFunction(data.title) ? data.title() : data.title
    );

    this.enableMultipleCreate = !!data.enableMultipleCreate;
    this.multipleCreateLabel = data.multipleCreateLabel;

    this.items = isFunction(data.formItems)
      ? data.formItems(data.resolved)
      : data.formItems;

    if (data.initialFormData) {
      this.formData = {
        ...(this.formData || {}),
        ...data.initialFormData
      };

      Object.entries(data.initialFormData).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          this.editedFields.add(key);
        }
      });
    }

    this.replaceItemEditorCallbacks(<DevExpress.ui.dxForm.Item[]>this.items);

    this.items = this.items.map((x) => {
      x.editorOptions = x.editorOptions
        ? {
            ...x.editorOptions,
            stylingMode: "filled"
          }
        : { stylingMode: "filled" };

      return x;
    });

    const initialCustomActions = data.customActionsFactory
      ? []
      : data.customActions;
    this.setButtons(initialCustomActions);

    const rejectButton: PopupButton = {
      title: data.rejectText || sntTranslate(marker("Annulla")),
      type: "text",
      onClick: () => this.onCancel()
    };

    this.secondaryButtons = signal([
      ...(data.rejectOnLeft ? [rejectButton] : []),
      ...(data.secondaryActions
        ? data.secondaryActions.map((action) => {
            return <PopupButton>{
              title: action.text,
              icon: action.icon,
              type: action.type,
              onClick: async (buttonClickAsyncEvent: ButtonClickAsyncEvent) => {
                await buttonClickAsyncEvent.execute(async () => {
                  await action.onClickAsync(
                    {
                      ...this.formData,
                      ...(this.data.mergeData || {})
                    },
                    this.onCancel,
                    (dirty: boolean) => {
                      this.dirty.next(dirty);
                    }
                  );
                });
              }
            };
          })
        : [])
    ]);
  }

  ngAfterViewInit(): void {
    if (this.data.onFormReady) {
      this.data.onFormReady({
        dxForm: this.dxForm,
        formData: this.formData,
        close: () => this.onCancel()
      });
    }

    if (this.data.customActionsFactory) {
      void this.updateCustomActionsFromFactory();
    }

    const firstItemToFocus =
      find(
        flattenTree(this.dxForm.items, (item) => item.items || []),
        (item) => item.editorOptions?.autoFocus
      ) ||
      searchForFirstDataFieldItem(this.dxForm.items, this.data.elementFocus);

    if (firstItemToFocus) {
      const editor = this.dxForm.instance.getEditor(firstItemToFocus.dataField);
      editor?.focus();
    }
  }

  private replaceItemEditorCallbacks(items: DevExpress.ui.dxForm.Item[]): void {
    items.forEach((item: DevExpress.ui.dxForm.Item) => {
      if (item.itemType === "group") {
        const groupItem = item as DevExpress.ui.dxForm.GroupItem;
        const groupItems = groupItem.items as DevExpress.ui.dxForm.Item[];
        if (groupItems?.length) {
          this.replaceItemEditorCallbacks(groupItems);
        }
      }

      const simpleItem = item as DevExpress.ui.dxForm.SimpleItem;
      if (simpleItem.editorOptions?.onValueChanged) {
        const originalOnChange = simpleItem.editorOptions
          .onValueChanged as (params: {
          dxEvent: unknown;
          dxForm: DxFormComponent;
          formData: unknown;
        }) => void;
        simpleItem.editorOptions = {
          ...simpleItem.editorOptions,
          onValueChanged: (dxEvent: unknown) => {
            const normalizedParams: {
              dxEvent: unknown;
              dxForm: DxFormComponent;
              formData: unknown;
            } & Record<string, unknown> = {
              ...(typeof dxEvent === "object" && dxEvent !== null
                ? (dxEvent as Record<string, unknown>)
                : {}),
              dxEvent,
              dxForm: this.dxForm,
              formData: this.formData
            };
            originalOnChange({
              ...normalizedParams
            });
          }
        };
      }

      if (simpleItem.editorOptions?.onFocusOut) {
        const originalOnFocus = simpleItem.editorOptions.onFocusOut as (
          form: DxFormComponent,
          data: unknown
        ) => void;
        simpleItem.editorOptions = {
          ...simpleItem.editorOptions,
          onFocusOut: (data: unknown) => {
            originalOnFocus(this.dxForm, data);
          }
        };
      }
    });
  }

  getSubtitle(): string {
    return "";
  }

  getTitle(): string {
    return isFunction(this.data.title)
      ? this.data.title(this.formData)
      : this.data.title;
  }

  isTitleVisible(): boolean {
    return true;
  }

  isCustomCheckboxVisible(): boolean {
    return this.enableMultipleCreate;
  }

  getCustomCheckboxLabel(): string | undefined {
    return this.multipleCreateLabel;
  }

  @Bind
  async onCancel(): Promise<void> {
    if (this.data.onCancel) {
      this.data.onCancel(this.formData);
    }

    await this.popupRef.close();
  }

  async onClosed(): Promise<void> {
    //TODO introdurre metodo onClosed nel PopupService
  }

  async onCustomAction(customAction: { onClick: () => void }): Promise<void> {
    await this.popupRef.close();
    customAction.onClick();
  }

  async onExecuteCustomAction(
    buttonClickAsyncEvent: ButtonClickAsyncEvent,
    customAction: {
      text: string;
      onClickAsync: (
        data: any,
        close: () => Promise<void>,
        setDirtyFn: (dirty: boolean) => void
      ) => Promise<void>;
      type?: "primary" | "default" | "text" | "outlined";
    },
    setDirtyFn: (dirty: boolean) => void
  ): Promise<void> {
    await buttonClickAsyncEvent.execute(async () => {
      await this.#validateAndContinue(
        (formData: any) => {
          return customAction.onClickAsync(
            {
              ...formData,
              ...(this.data.mergeData || {})
            },
            this.onCancel,
            setDirtyFn
          );
        },
        {
          multipleCreate: false
        }
      );
    });
  }

  async onGoToForm(): Promise<void> {
    if (this.data.goToForm) {
      if (this.data.disabledValidationForGoToForm) {
        await this.#continueWithoutValidation(async (formData: any) => {
          this.dirty.next(false);
          if (this.data.goToForm) {
            await this.data.goToForm(formData);
          }
        });
      } else {
        await this.#validateAndContinue(
          async (formData: any) => {
            this.dirty.next(false);
            if (this.data.goToForm) {
              await this.data.goToForm(formData);
            }
          },
          {
            multipleCreate: false
          }
        );
      }
    }
  }

  async onConfirm(
    buttonClickAsyncEvent: ButtonClickAsyncEvent,
    { multipleCreate }: { multipleCreate: boolean | undefined }
  ): Promise<void> {
    await buttonClickAsyncEvent.execute(async () => {
      await this.#validateAndContinue(
        async (formData: any) => {
          this.dirty.next(false);
          try {
            const result = await this.data.create(
              {
                ...formData,
                ...(this.data.mergeData || {})
              },
              false,
              {
                multipleCreate
              }
            );

            return result;
          } catch (ex) {
            this.dirty.next(true);
            throw ex;
          }
        },
        { multipleCreate }
      );
    });
  }

  onFieldDataChanged(dxEvent: FieldDataChangedEvent): void {
    if (this.data.onFormDataChange) {
      this.data.onFormDataChange(this.formData);
    }

    if (!dxEvent) {
      return;
    }

    if (dxEvent.dataField) {
      this.editedFields.add(dxEvent.dataField);
    }

    if (isFunction(this.data.title)) {
      this.popupTitle.set(this.data.title(this.formData));
    }

    if (
      this.data.customActionsFactory &&
      this.data.customActionsFactoryDataField === dxEvent.dataField
    ) {
      void this.updateCustomActionsFromFactory();
    }

    setTimeout(() => {
      this.dirty.next(true);
    });
  }

  private async updateCustomActionsFromFactory(): Promise<void> {
    const factory = this.data.customActionsFactory;
    if (!factory) {
      return;
    }

    const actions = await Promise.resolve(
      factory({
        ...this.formData,
        ...(this.data.mergeData || {})
      })
    );
    this.setButtons(actions ?? []);
  }

  private setButtons(
    customActions?: Array<{
      text: string;
      icon?: string;
      onClickAsync: (
        data: any,
        close: () => Promise<void>,
        setDirtyFn: (dirty: boolean) => void
      ) => Promise<void>;
      type?: "primary" | "default" | "text" | "outlined";
    }>
  ): void {
    const data = this.data;
    this.buttons.set([
      ...(data.rejectOnLeft
        ? []
        : [
            {
              title: data.rejectText || sntTranslate(marker("Annulla")),
              type: "text" as const,
              onClick: () => this.onCancel()
            }
          ]),
      ...(customActions?.map((customAction) => {
        return <PopupButton>{
          title: customAction.text,
          icon: customAction.icon,
          type: customAction.type,
          onClick: async (buttonClickAsyncEvent: ButtonClickAsyncEvent) => {
            await this.onExecuteCustomAction(
              buttonClickAsyncEvent,
              customAction,
              (dirty: boolean) => {
                this.dirty.next(dirty);
              }
            );
          }
        };
      }) ?? []),
      ...(data.goToForm
        ? [
            <PopupButton>{
              title: sntTranslate(marker("Vai al form completo")),
              type: "default",
              onClick: async (buttonClickAsyncEvent: ButtonClickAsyncEvent) =>
                await buttonClickAsyncEvent.execute(async () => {
                  await this.onGoToForm();
                })
            },
            <PopupButton>{
              title: sntTranslate(marker("Crea")),
              type: "primary",
              icon: data.confirmIcon,
              onClick: async (buttonClickAsyncEvent: ButtonClickAsyncEvent) =>
                await this.onConfirm(buttonClickAsyncEvent, {
                  multipleCreate: false
                })
            }
          ]
        : [
            <PopupButton>{
              title: data.confirmText || sntTranslate(marker("Conferma")),
              type: "primary",
              icon: data.confirmIcon,
              onClick: async (
                buttonClickAsyncEvent: ButtonClickAsyncEvent,
                { checkboxValue }
              ) => {
                return await this.onConfirm(buttonClickAsyncEvent, {
                  multipleCreate: checkboxValue
                });
              }
            }
          ])
    ]);
  }

  #getChangedFormData(): Record<string, unknown> {
    const result: Record<string, unknown> = {};
    for (const dataField of this.editedFields) {
      const value = get(this.formData, dataField);
      set(result, dataField, value);
    }
    return result;
  }

  async #continueWithoutValidation(
    continueFunc: (data: any) => Promise<void>
  ): Promise<void> {
    if (Object.keys(this.formData).length === 0) {
      const result = {
        id: uuid()
      };
      await this.popupRef.confirm(result);
    }

    await continueFunc(this.formData);
    const result = {
      id: uuid()
    };
    await this.popupRef.confirm(result);
  }

  async #validateAndContinue(
    continueFunc: (data: any) => Promise<void>,
    { multipleCreate }: { multipleCreate: boolean | undefined }
  ): Promise<void> {
    let validation = this.dxForm.instance.validate();
    if (validation.complete) {
      validation = await validation.complete;
    }
    if (!validation.isValid) {
      return;
    }

    await continueFunc(this.#getChangedFormData());
    const result = {
      id: uuid()
    };

    if (!multipleCreate) {
      // chiude il popup
      await this.popupRef.confirm(result);
    }
  }
}

function searchForFirstDataFieldItem(
  items: FormItem[],
  elementFocus?: string
): DevExpress.ui.dxFormSimpleItem | undefined {
  for (const item of items) {
    if (!elementFocus) {
      return item;
    }

    if ((<DevExpress.ui.dxFormSimpleItem>item).dataField === elementFocus) {
      return item;
    }

    const childItems = (<DevExpress.ui.dxFormGroupItem>item).items;
    if (childItems) {
      const validChildItem = searchForFirstDataFieldItem(
        childItems,
        elementFocus
      );
      if (validChildItem) {
        return validChildItem;
      }
    }
  }

  return undefined;
}
